import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FaFileAlt, FaDownload, FaFilter, FaSearch, FaClock, FaUser } from 'react-icons/fa';
import api from '../../services/api';

// jQuery and DataTables are loaded via CDN in index.html
const $ = window.jQuery;

const AdminLoginLogs = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: '',
        user_id: '',
        search: ''
    });
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);

    useEffect(() => {
        loadUsers();
        loadLogs();
    }, []);

    useEffect(() => {
        if (logs.length > 0 && tableRef.current && !dataTableRef.current) {
            initializeDataTable();
        }
    }, [logs]);

    const loadUsers = async () => {
        try {
            const response = await api.get('/api/admin/users');
            if (response.data.success) {
                setUsers(response.data.data || []);
            }
        } catch (err) {
            console.error('Error loading users:', err);
        }
    };

    const loadLogs = async () => {
        try {
            setLoading(true);
            setError('');

            const params = new URLSearchParams();
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            if (filters.user_id) params.append('user_id', filters.user_id);
            if (filters.search) params.append('search', filters.search);

            const response = await api.get(`/api/login-logs/all-logs?${params.toString()}`);
            
            if (response.data.success) {
                setLogs(response.data.data || []);
                
                if (dataTableRef.current) {
                    dataTableRef.current.destroy();
                    dataTableRef.current = null;
                }
                
                setTimeout(() => {
                    initializeDataTable();
                }, 100);
            } else {
                setError(response.data.message || 'Failed to load login logs');
            }
        } catch (err) {
            console.error('Error loading login logs:', err);
            setError(err.response?.data?.message || 'Failed to load login logs');
        } finally {
            setLoading(false);
        }
    };

    const initializeDataTable = () => {
        if (tableRef.current && !dataTableRef.current && typeof $ !== 'undefined' && $.fn.DataTable) {
            dataTableRef.current = $(tableRef.current).DataTable({
                data: logs,
                columns: [
                    {
                        data: 'created_at',
                        title: 'Login Date & Time',
                        render: function(data) {
                            if (!data) return '-';
                            const date = new Date(data);
                            return date.toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                            });
                        }
                    },
                    {
                        data: 'user_name',
                        title: 'User Name',
                        render: function(data, type, row) {
                            return data || `ID: ${row.user_id}`;
                        }
                    },
                    {
                        data: 'user_email',
                        title: 'Email'
                    },
                    {
                        data: 'user_role',
                        title: 'Role',
                        render: function(data) {
                            const colors = {
                                'admin': 'danger',
                                'distributor': 'warning',
                                'user': 'info'
                            };
                            const color = colors[data] || 'secondary';
                            return `<span class="badge bg-${color}">${data || 'user'}</span>`;
                        }
                    },
                    {
                        data: 'ip_address',
                        title: 'IP Address'
                    },
                    {
                        data: 'device_type',
                        title: 'Device Type',
                        render: function(data) {
                            const colors = {
                                'Mobile': 'primary',
                                'Tablet': 'info',
                                'Desktop': 'success'
                            };
                            const color = colors[data] || 'secondary';
                            return `<span class="badge bg-${color}">${data || 'Unknown'}</span>`;
                        }
                    },
                    {
                        data: 'user_agent',
                        title: 'Browser/Device',
                        render: function(data) {
                            if (!data) return '-';
                            const ua = data.toLowerCase();
                            if (ua.includes('chrome')) return 'Chrome';
                            if (ua.includes('firefox')) return 'Firefox';
                            if (ua.includes('safari')) return 'Safari';
                            if (ua.includes('edge')) return 'Edge';
                            return data.substring(0, 50) + (data.length > 50 ? '...' : '');
                        }
                    },
                    {
                        data: 'time_ago',
                        title: 'Time Ago'
                    }
                ],
                order: [[0, 'desc']],
                pageLength: 25,
                lengthMenu: [[10, 25, 50, 100, -1], [10, 25, 50, 100, 'All']],
                dom: 'Bfrtip',
                buttons: [
                    'copy', 'csv', 'excel', 'pdf', 'print'
                ],
                responsive: true,
                scrollX: true,
                language: {
                    search: 'Search:',
                    lengthMenu: 'Show _MENU_ entries',
                    info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                    infoEmpty: 'No entries to show',
                    infoFiltered: '(filtered from _MAX_ total entries)'
                }
            });
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleApplyFilters = (e) => {
        e.preventDefault();
        loadLogs();
    };

    const handleResetFilters = () => {
        setFilters({
            start_date: '',
            end_date: '',
            user_id: '',
            search: ''
        });
        setTimeout(() => {
            loadLogs();
        }, 100);
    };

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">
                    <FaClock className="me-2" />
                    All Users Login Logs (Last 200 Days)
                </h2>
            </div>

            {/* Filters */}
            <Card className="mb-4">
                <Card.Header>
                    <FaFilter className="me-2" />
                    Filters
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleApplyFilters}>
                        <Row>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="start_date"
                                        value={filters.start_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="end_date"
                                        value={filters.end_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>User</Form.Label>
                                    <Form.Select
                                        name="user_id"
                                        value={filters.user_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Users</option>
                                        {users.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.name} ({user.email})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Search</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="search"
                                        placeholder="Name, Email, IP..."
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                                <Button type="submit" variant="primary" className="me-2 w-100">
                                    <FaSearch className="me-1" />
                                    Apply
                                </Button>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col>
                                <Button type="button" variant="secondary" onClick={handleResetFilters}>
                                    Reset Filters
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Login Logs Table */}
            <Card>
                <Card.Header>
                    <FaUser className="me-2" />
                    Login History ({logs.length} records)
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading login logs...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table
                                ref={tableRef}
                                className="table table-striped table-hover"
                                style={{ width: '100%' }}
                            >
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminLoginLogs;

